// AddField.tsx

import { useState, useEffect } from 'react';
import Dropdown from '../../../../../components/Dropdown/Dropdown';
import CustomTextInput from '../../InputFields/CustomTextInput';
import CustomToggle from '../../InputFields/CustomToggel';
import TextDataType from '../FieldInput/TextDataType';
import TextAreaDataType from '../FieldInput/TextAreaDataType';
import DropdownDataType from '../FieldInput/DropdownDataType';
import DateDataType from '../FieldInput/DateDataType';
import Button from '../../../../../components/Button/Button';
import { useToast } from '../../../../../components/Toast/ToastContext';
import {
  usePostFieldMutation,
  useUpdateFieldMutation,
  usePostTabsMutation,
  useUpdateTabsMutation,
  usePostTableMutation,
  useUpdateTableMutation,
} from '../../../../../services/fieldApi/FieldApi';
import { useParams } from 'react-router-dom';
// import { ISectionItem } from '../../../../../services/plaintiffSheets/plaintiffSheetsTypes';
import ConditionDataType from '../FieldInput/ConditionDataType';
import InformationDataType from '../FieldInput/InformationDataType';
import NumberInput from '../../InputFields/NumberInput';
import TableDataType from '../FieldInput/TableDataType';
import TabsDataType from '../FieldInput/TabsDataType';
import Icons from '../../../../../assets/index';
import UploadFileDataType from '../FieldInput/UploadFileDataType';
import LabelDataType from '../FieldInput/LabelDataType';
import ReadOnlyTextAreaType from '../FieldInput/ReadOnlyTextAreaType';
import { v4 as uuidv4 } from 'uuid';

const FieldOptions = [
  { value: 'text_input', label: 'Text input' },
  { value: 'text_area', label: 'Text Area' },
  { value: 'readonly_text_area', label: 'Read Only TextArea' },
  { value: 'label', label: 'Label' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'file', label: 'File Upload' },
  { value: 'condition_with_multiple_fields', label: 'Condition' },
  { value: 'information', label: 'Information' },
  { value: 'table', label: 'Table' },
  { value: 'tabs', label: 'Tabs' },
];

interface IAddSectionProps {
  onClose: () => void;
  factSheetId?: string;
  data?: any;
  sectionId?: string;
}

type Column = {
  label: string;
  dataType: 'Text Input' | 'Number' | 'Date' | 'Dropdown';
};

type TableData = {
  columns: Column[];
  rows: string[];
  groupConfig: {
    groupName: string;
    selectedColumns: number[];
  };
  tabName?: string;
};

const AddField: React.FC<IAddSectionProps> = ({ onClose, data, sectionId }) => {
  const { addToast } = useToast();
  const { id } = useParams();
  const isEditMode = data?.field_id;

  const [AddField, { isLoading: addLoading }] = usePostFieldMutation();
  const [AddTable] = usePostTableMutation();
  const [UpdateTable] = useUpdateTableMutation();

  const [UpdateField, { isLoading: updateLoading }] = useUpdateFieldMutation();
  const [PostTabs, { isLoading: addTabsLoading }] = usePostTabsMutation();
  const [UpdateTabs, { isLoading: updateTabsLoading }] = useUpdateTabsMutation();

  // Initialize form state with existing field data if in edit mode
  const [formData, setFormData] = useState({
    name: data?.label || '',
    placeholder: data?.additional_config?.placeholder || '',
    defaultValue: data?.additional_config?.defaultValue || '',
    helpText: data?.additional_config?.helpText || '',
    informationContainerContent:
      data?.additional_config?.informationContainerContent ||
      data?.additional_config?.modalContent ||
      '',
    modalContent: data?.additional_config?.modalContent || '',
    beforeLinkDescription: data?.additional_config?.beforeLinkDescription || '',
    inlineTriggerText: data?.additional_config?.inlineTriggerText || '',
    date: data?.additional_config?.dateFormat || '',
    options: Array.isArray(data.options)
      ? data.options.map((opt: any) => ({
          id: opt.id || uuidv4(),
          name: opt.label || opt.name || '',
          fields: Array.isArray(opt.fields)
            ? opt.fields.map((field: any) => ({
                id: field.id || uuidv4(),
                label: field.label || '',
                fieldType: field.fieldType || field.field_type || '',
                field_type: field.field_type || field.fieldType || '',
                required: field.required ?? true,
                additional_config: field.additional_config || {
                  editable: true,
                  placeholder: '',
                  defaultValue: '',
                },
              }))
            : [],
        }))
      : [],
    tabName: data?.tabName,
  });

  // Initialize with data.field_type instead of data?.field_type
  const [selected, setSelected] = useState(data?.field_type || '');
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [tableErrors, setTableErrors] = useState<{ [key: string]: string }>({});
  const [tabsErrors, setTabsErrors] = useState<{ [key: string]: string }>({});

  // Initialize all toggle states with proper data mapping
  const [isMandatory, setIsMandatory] = useState(data?.is_required);
  const [isEditable, setIsEditable] = useState(
    data?.additional_config?.editable !== undefined ? data.additional_config.editable : false
  );
  const [isModal, setIsModal] = useState(
    Boolean(
      data?.additional_config?.inlineTriggerText || data?.additional_config?.beforeLinkDescription
    )
  );
  const [columns, setColumns] = useState(
    data?.additional_config?.dimensions?.columns || (data?.columns ? data.columns.length : 1)
  );
  const [tabs, setTabs] = useState(data?.tabsNumber || (data?.tabsName ? data.tabsName.length : 1));
  const [rows, setRows] = useState(
    data?.additional_config?.dimensions?.rows || (data?.rows ? data.rows.length : 1)
  );
  const [isRowsHeader, setIsRowsHeader] = useState(
    data?.additional_config?.headers?.rowHeader !== undefined
      ? data.additional_config.headers.rowHeader
      : false
  );
  const [isColumnsHeader, setIsColumnsHeader] = useState(
    data?.additional_config?.headers?.columnHeader !== undefined
      ? data.additional_config.headers.columnHeader
      : true
  );
  const [isColumnsGrouping, setIsColumnsGrouping] = useState(
    data?.additional_config?.columnGroupingEnabled ||
      (data?.columnGroups && data.columnGroups.length > 0) ||
      false
  );
  const [isAdditionalRows, setIsAdditionalRows] = useState(
    data?.additional_config?.additionalRows !== undefined
      ? data.additional_config.additionalRows
      : false
  );

  const [isPopulateData, setIsPopulateData] = useState(
    data?.ispopulated !== undefined ? data.ispopulated : false
  );

  // Initialize table data with more robust handling
  const [tableData, setTableData] = useState<TableData>(() => {
    // Default empty table data
    const defaultTableData: TableData = {
      columns: [],
      rows: [],
      groupConfig: {
        groupName: '',
        selectedColumns: [],
      },
      tabName: '',
    };

    // If we have data, populate the table data accordingly
    if (data) {
      // Handle columns
      if (data.columns && Array.isArray(data.columns)) {
        defaultTableData.columns = data.columns.map((col: any) => ({
          label: col.label || '',
          dataType: col.data_type,
        }));
      }

      defaultTableData.tabName = data?.tabName;

      // Handle rows

      if (data.tabs && Array.isArray(data.tabs) && data.field_type === 'tabs') {
        defaultTableData.rows = data.tabs.map((tab: { name: string }) => tab.name);
      }
      if (data.rows && Array.isArray(data.rows) && data.field_type === 'table') {
        defaultTableData.rows = data.rows.map((row: { label: string }) => row.label);
      }

      if (data.columnGroups && data.columnGroups.length > 0) {
        // Handle column grouping
        const group = data.columnGroups[0];
        defaultTableData.groupConfig.groupName = group.name || '';

        // Map column names to indices
        if (group.columns && Array.isArray(group.columns) && defaultTableData.columns.length > 0) {
          defaultTableData.groupConfig.selectedColumns = group.columns
            .map((colName: string) => {
              const index = defaultTableData.columns.findIndex((c) => c.label === colName);
              return index !== -1 ? index : null;
            })
            .filter((index: number | null) => index !== null) as number[];
        }
      }
    }

    return defaultTableData;
  });

  const [shouldValidate, setShouldValidate] = useState(false);

  // Effect to update formData when field type changes
  useEffect(() => {
    if (data && selected !== data.field_type) {
      // Reset relevant state when field type changes
      setFormData((prev) => ({
        ...prev,
        options: [{ name: '' }],
        placeholder: '',
        defaultValue: '',
        helpText: '',
      }));
      setTableData({
        columns: [],
        rows: [],
        groupConfig: {
          groupName: '',
          selectedColumns: [],
        },
        tabName: '',
      });
    }
  }, [selected, data]);

  // Make sure we properly populate the form when data changes (e.g. edit mode activated)
  useEffect(() => {
    if (data) {
      // Update formData with all properties from data
      setFormData({
        name: data.label || '',
        placeholder: data?.additional_config?.placeholder || '',
        defaultValue: data?.additional_config?.defaultValue || '',
        helpText: data?.additional_config?.helpText || '',
        informationContainerContent:
          data?.additional_config?.informationContainerContent ||
          data?.additional_config?.modalContent ||
          '',
        modalContent: data?.additional_config?.modalContent || '',
        beforeLinkDescription: data?.additional_config?.beforeLinkDescription || '',
        inlineTriggerText: data?.additional_config?.inlineTriggerText || '',
        date: data?.additional_config?.dateFormat || '',
        options: Array.isArray(data.options)
          ? data.options.map((opt: any) => ({
              name: opt.label || opt.name || '',
              fields: Array.isArray(opt.fields)
                ? opt.fields.map((field: any) => ({
                    label: field.label || '',
                    field_type: field.field_type || '',
                    required: field.required ?? false,
                    additional_config: field.additional_config || {},
                  }))
                : [],
              defaultValue: opt.field?.additional_config?.defaultValue || '',
            }))
          : [],
        tabName: data?.tabName,
      });

      // Update selected field type
      setSelected(data.field_type || '');

      // Update toggle states
      setIsMandatory(data.is_required !== undefined ? data?.is_required : false);
      setIsEditable(
        data?.additional_config?.editable !== undefined ? data.additional_config.editable : false
      );
      setIsModal(
        Boolean(
          data?.additional_config?.inlineTriggerText ||
            data?.additional_config?.beforeLinkDescription
        )
      );

      // Update table/tabs configuration
      if (data.field_type === 'table') {
        setColumns(
          data?.additional_config?.dimensions?.columns || (data?.columns ? data.columns.length : 1)
        );
        setRows(data?.additional_config?.dimensions?.rows || (data?.rows ? data.rows.length : 1));
        setIsRowsHeader(
          data?.additional_config?.headers?.rowHeader !== undefined
            ? data.additional_config.headers.rowHeader
            : false
        );
        setIsColumnsHeader(
          data?.additional_config?.headers?.columnHeader !== undefined
            ? data.additional_config.headers.columnHeader
            : true
        );
        setIsColumnsGrouping(
          data?.additional_config?.columnGroupingEnabled ||
            (data?.columnGroups && data.columnGroups.length > 0) ||
            false
        );
        setIsAdditionalRows(
          data?.additional_config?.additionalRows !== undefined
            ? data.additional_config.additionalRows
            : false
        );
        setIsPopulateData(data?.ispopulated ? data?.ispopulated : false);
      } else if (data.field_type === 'tabs') {
        setTabs(data?.tabs?.length || 1);
      }
    }
  }, [data]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    const updatedErrors = { ...errors, [name]: '' };

    setFormData(updatedFormData);
    setErrors(updatedErrors);
  };

  const handleChildDataChange = (updatedValues: Partial<any>) => {
    const updatedFormData = {
      ...formData,
      ...updatedValues,
    };

    const updatedErrors = { ...errors };

    if (updatedValues.options) {
      const allFilled = updatedValues.options.every((opt: any) => opt.name.trim() !== '');
      if (allFilled) {
        delete updatedErrors.dropdown;
        delete updatedErrors.emptyField;
      }
    }

    setFormData(updatedFormData);
    setErrors(updatedErrors);
  };

  const handlefield_typeChange = (value: string) => {
    setSelected(value);
    setError(false);
    setTableErrors({});
  };

  const handleTableDataChange = (data: TableData) => {
    setTableData(data);
    setTableErrors({});
  };

  const handleTabsDataChange = (data: any) => {
    setTableData(data);
    setTabsErrors({});
  };

  const handleSaveField = async () => {
    setShouldValidate(true);
    const newErrors: any = {};
    const newTableErrors: { [key: string]: string } = {};
    const newTabErrors: { [key: string]: string } = {};
    let payload: any = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Please provide a relevant field name.';
    }

    if (!formData.modalContent.trim() && selected === 'information' && isModal) {
      newErrors.modalContent = 'Please provide modal content.';
    }
    if (!formData.beforeLinkDescription.trim() && selected === 'information' && isModal) {
      newErrors.beforeLinkDescription = 'Please provide relevant description.';
    }

    if (!formData.informationContainerContent.trim() && selected === 'information' && !isModal) {
      newErrors.informationContainerContent = 'Please provide information container content.';
    }

    const allFieldsFilled = (formData?.options ?? []).every(
      (input: any) => input.name.trim() !== ''
    );

    if (
      formData?.options?.length === 1 &&
      formData?.options[0]?.name?.trim() === '' &&
      selected === 'dropdown'
    ) {
      newErrors.dropdown = 'Please mention at least one option to use this field effectively.';
    }

    if (formData?.options?.length > 1 && selected === 'condition_with_multiple_fields') {
      formData.options.forEach((item: { name: string; fields: any[] }) => {
        if (item?.name?.trim() && Array.isArray(item.fields) && item.fields.length > 0) {
          setShouldValidate(true);
          return;
        } else {
          setShouldValidate(false);
        }
      });
    }

    if (!allFieldsFilled && selected === 'dropdown') {
      newErrors.emptyField = 'Option field can not be blank';
    }

    if (!selected) {
      setError(true);
    } else {
      setError(false);
    }

    // Validate table fields
    if (selected === 'table') {
      let hasTableErrors = false;

      if (isRowsHeader) {
        tableData.rows.forEach((row, index) => {
          if (!row.trim()) {
            newTableErrors[`row_${index}`] = 'Please provide a relevant row header.';
            hasTableErrors = true;
          }
        });
      }

      if (isColumnsHeader) {
        tableData.columns.forEach((col, index) => {
          if (!col.label.trim()) {
            newTableErrors[`column_${index}`] = 'Please provide a column name.';
            hasTableErrors = true;
          }
          if (!col.dataType) {
            newTableErrors[`columnType_${index}`] =
              'Column type cannot be empty. Please select an option.';
            hasTableErrors = true;
          }
        });
      }

      if (isColumnsGrouping) {
        if (!tableData.groupConfig.groupName.trim()) {
          newTableErrors.groupName = 'Please provide a group name.';
          hasTableErrors = true;
        }

        if (tableData.groupConfig.selectedColumns?.length === 0) {
          newTableErrors.selectedColumns = 'Please select at least one column.';
          hasTableErrors = true;
        }
      }

      setTableErrors(newTableErrors);

      if (hasTableErrors) {
        newErrors.table = 'Please fix all table field errors.';
      }
    }

    if (selected === 'tabs') {
      let hasTabErrors = false;

      tableData.rows.forEach((row, index) => {
        if (!row.trim()) {
          newTabErrors[`tab_${index}`] = 'Please provide a relevant tab name.';
          hasTabErrors = true;
        }
      });

      setTabsErrors(newTabErrors);

      if (hasTabErrors) {
        newErrors.tab = 'Please fix all tab field errors.';
      }
    }

    if (Object.keys(newErrors).length > 0 || Object.keys(newTableErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare payload based on field type
    switch (selected) {
      case 'text_input':
        payload = {
          label: formData.name,
          fieldType: selected,
          required: isMandatory,
          config: {
            editable: isEditable,
            placeholder: formData.placeholder,
            defaultValue: formData.defaultValue,
            helpText: formData.helpText,
          },
        };
        break;

      case 'readonly_text_area':
        payload = {
          label: formData.name,
          fieldType: selected,
          config: {
            editable: isEditable,
            defaultValue: formData.defaultValue,
          },
        };
        break;
      case 'text_area':
        payload = {
          label: formData.name,
          fieldType: selected,
          required: isMandatory,
          config: {
            editable: isEditable,
            placeholder: formData.placeholder,
            defaultValue: formData.defaultValue,
            helpText: formData.helpText,
          },
        };
        break;

      case 'dropdown':
        payload = {
          label: formData.name,
          fieldType: selected,
          required: isMandatory,
          options: formData.options.map((item: any) => ({
            label: item.name,
            value: item.name,
          })),
          config: {
            editable: isEditable,
            placeholder: formData.placeholder,
            defaultValue: formData.defaultValue,
            helpText: formData.helpText,
          },
        };
        break;

      case 'file':
        payload = {
          label: formData.name,
          fieldType: selected,
          required: isMandatory,
          config: {
            helpText: formData.helpText,
          },
        };
        break;

      case 'label':
        payload = {
          label: formData.name,
          fieldType: selected,
          required: isMandatory,
          config: {
            helpText: formData.helpText,
          },
        };
        break;

      case 'condition_with_multiple_fields':
        payload = {
          label: formData.name,
          fieldType: selected,
          required: isMandatory,
          config: {
            helpText: formData.helpText,
            defaultValue: formData.defaultValue || '',
            placeholder: formData.placeholder,
          },
          options: formData.options.map((option: any) => ({
            id: option.id || uuidv4(),
            label: option.name,
            value: option.name.toLowerCase(),
            fields: Array.isArray(option.fields)
              ? option.fields.map((field: any) => ({
                  id: field.id || uuidv4(),
                  label: field.label || option.name,
                  fieldType: field.field_type || field.fieldType,
                  field_type: field.field_type || field.fieldType,
                  required: field.required ?? true,
                  additional_config: field.additional_config || {
                    editable: true,
                    placeholder: '',
                    defaultValue: '',
                  },
                }))
              : [],
          })),
        };
        break;

      case 'information':
        payload = {
          label: formData.name,
          fieldType: selected,
          required: false,
          tabName: formData?.tabName,
          ispopulated: isPopulateData,
          config: isModal
            ? {
                beforeLinkDescription: formData.beforeLinkDescription,
                inlineTriggerText: formData.inlineTriggerText,
                modalContent: formData.modalContent,
              }
            : {
                modalContent: formData.informationContainerContent,
              },
        };
        break;

      case 'date':
        payload = {
          label: formData.name,
          fieldType: selected,
          required: isMandatory,
          config: {
            placeholder: formData.placeholder,
            helpText: formData.helpText,
            dateFormat: formData.date,
          },
        };
        break;

      case 'table':
        payload = {
          label: formData.name,
          required: isMandatory,
          tabName: tableData?.tabName,
          ispopulated: isPopulateData,
          columnHeaders:
            tableData.columns.some((col) => !col.label || !col.dataType) || !isColumnsHeader
              ? []
              : tableData.columns,
          rowHeaders:
            tableData.rows.some((row) => row === '') || !isRowsHeader ? [] : tableData.rows,
          columnGroups: isColumnsGrouping
            ? [
                {
                  name: tableData?.groupConfig?.groupName,
                  columns: isColumnsGrouping
                    ? tableData.groupConfig.selectedColumns
                        .map((index) => tableData.columns[index]?.label)
                        .filter((label) => !!label)
                    : [],
                },
              ]
            : [],
          config: {
            dimensions: {
              columns: tableData.columns.some((col) => !col.label || !col.dataType)
                ? 1
                : tableData?.columns?.length,
              rows: tableData?.rows?.length,
            },
            headers: {
              rowHeader: isRowsHeader,
              columnHeader: isColumnsHeader,
            },
            columnGroupingEnabled: isColumnsGrouping,
            additionalRows: isAdditionalRows,
          },
        };
        break;

      case 'tabs':
        payload = {
          label: formData.name,
          fieldType: selected,
          required: isMandatory,
          tabsCount: tableData.rows.length,
          tabsName: tableData.rows,
        };
        break;

      default:
        break;
    }

    try {
      let response;
      if (selected !== 'tabs' && selected !== 'table') {
        if (isEditMode && data) {
          response = await UpdateField({
            factSheetId: id,
            sectionId: sectionId,
            fieldId: data.field_id,
            data: payload,
          }).unwrap();
        } else {
          response = await AddField({
            factSheetId: id,
            sectionId: sectionId,
            data: payload,
          }).unwrap();
        }
      } else if (selected === 'table') {
        if (isEditMode && data) {
          response = await UpdateTable({
            factSheetId: id,
            sectionId: sectionId,
            tableId: isEditMode,
            data: payload,
          }).unwrap();
        } else {
          response = await AddTable({
            factSheetId: id,
            sectionId: sectionId,
            data: payload,
          }).unwrap();
        }
      } else {
        if (isEditMode && data) {
          response = await UpdateTabs({
            factSheetId: id,
            sectionId: sectionId,
            tabId: data.field_id,
            data: payload,
          }).unwrap();
        } else {
          response = await PostTabs({
            factSheetId: id,
            sectionId: sectionId,
            data: payload,
          }).unwrap();
        }
      }

      if (response.status === 'success') {
        addToast(
          isEditMode ? 'Field Updated' : 'Field Added',
          isEditMode ? 'Field updated successfully.' : 'Field added successfully.',
          'success'
        );
        onClose();
      }
    } catch (error) {
      selected === 'table' || selected === 'information'
        ? addToast(
            isEditMode ? 'Table Update Failed' : 'Table Addition Failed',
            'Data Identifier Name does not match with the existing table name. Please try again.',
            'error'
          )
        : addToast(
            isEditMode ? 'Update Failed' : 'Addition Failed',
            isEditMode
              ? 'Failed to update field. Please try again.'
              : 'Failed to add field. Please try again.',
            'error'
          );
    }
  };

  const handleClearAll = () => {
    setFormData({
      name: '',
      helpText: '',
      defaultValue: '',
      placeholder: '',
      options: [{ id: 1, name: '', fields: [{ id: 1, field_type: '' }] }],
      informationContainerContent: '',
      beforeLinkDescription: '',
      inlineTriggerText: '',
      modalContent: '',
      date: '',
      tabName: '',
    });
    setSelected('');
    setIsMandatory(false);
    setIsEditable(false);
    setErrors({});
    setTableErrors({});
    setError(false);
    setIsAdditionalRows(false);
    setIsColumnsHeader(true);
    setIsColumnsGrouping(false);
    setIsRowsHeader(false);
    setIsModal(false);
    setTableData({
      columns: [],
      rows: [],
      groupConfig: {
        groupName: '',
        selectedColumns: [],
      },
      tabName: '',
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-1 overflow-hidden">
        <div className="w-[49%] p-1 overflow-y-auto">
          <div className="mb-2">
            <CustomTextInput
              label="Field Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Please enter relevant field name"
              required
              description="This will show up as the label of the form field."
              error={errors.name}
              className="text-ds-gray-500 font-semibold text-xs "
            />
          </div>

          <div className="mb-2">
            <label className="text-xs text-ds-gray-500 font-semibold">
              Field Type <span className="text-ds-red-400">*</span>
            </label>
            <p className="mt-1 text-xs text-ds-gray-300 mb-3">
              You can specify the type of data you expect people to enter; text, number, date,
              checkbox etc.
            </p>

            <Dropdown
              options={FieldOptions}
              selectedValue={selected}
              onChange={handlefield_typeChange}
              error={error}
              errorMessage="Field type cannot be empty. Please select an option from the dropdown list."
            />
          </div>

          {selected !== '' && selected !== 'table' && (
            <>
              {selected !== 'information' &&
                selected !== 'tabs' &&
                selected !== 'readonly_text_area' && (
                  <div className="mb-4">
                    <CustomToggle
                      label="Mandatory Field"
                      description="Enable this if the person capturing the data should not miss this."
                      checked={isMandatory}
                      onChange={setIsMandatory}
                      className="pr-3"
                    />
                  </div>
                )}

              {(selected === 'information' || selected === 'table' || selected === 'tabs') && (
                <>
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-4 bg-ds-mc-blue-100 px-3 py-4 rounded-md">
                      <span>
                        <Icons.infoIcon />
                      </span>
                      <p className="text-xs text-ds-orange-900">
                        Field Name works as the title of the modal and does not appear for
                        information, table and tabs.
                      </p>
                    </div>
                  </div>
                  {selected === 'information' && (
                    <>
                      <div className="mb-4">
                        <CustomToggle
                          label="Modal View"
                          description="Allow this information type to display a trigger text that opens a modal."
                          checked={isModal}
                          onChange={setIsModal}
                          className="pr-3"
                        />
                      </div>
                      <div className="mb-4">
                        <CustomToggle
                          label="Data Auto-population"
                          description="Allows user to auto-populate data to the specific field with the help of identifier."
                          checked={isPopulateData}
                          onChange={setIsPopulateData}
                          className="pr-3"
                        />
                      </div>
                    </>
                  )}
                  {isModal && (
                    <>
                      <div>
                        <CustomTextInput
                          label="Description Before Link"
                          name="beforeLinkDescription"
                          value={formData.beforeLinkDescription}
                          onChange={handleInputChange}
                          placeholder="Please provide relevant description"
                          required
                          description="You can specify a short message before the hyperlink to provide context."
                          error={errors.beforeLinkDescription}
                          className="text-ds-gray-500 font-semibold text-xs"
                        />
                      </div>
                      <div>
                        <CustomTextInput
                          label="Inline Trigger Text"
                          name="inlineTriggerText"
                          value={formData.inlineTriggerText}
                          onChange={handleInputChange}
                          placeholder="Please provide relevant trigger text"
                          description="You can specify the trigger text that appears as the hyperlink."
                          className="text-ds-gray-500 font-semibold text-xs"
                        />
                      </div>
                    </>
                  )}
                </>
              )}
              {selected !== 'information' &&
                selected !== 'tabs' &&
                selected !== 'file' &&
                selected != 'label' && (
                  <>
                    {!['date', 'condition_with_multiple_fields', 'readonly_text_area'].includes(
                      selected
                    ) && (
                      <div className="mb-4">
                        <CustomToggle
                          label="Editable Field"
                          description="Allow users to modify the value. If disabled, the field will be read-only."
                          checked={isEditable}
                          onChange={setIsEditable}
                          className="pr-3"
                        />
                      </div>
                    )}

                    {selected !== 'file' && selected !== 'readonly_text_area' && (
                      <div className="mb-4">
                        <CustomTextInput
                          label="Placeholder Value"
                          name="placeholder"
                          value={formData.placeholder}
                          onChange={handleInputChange}
                          placeholder="Please provide placeholder value"
                          description="Provide a placeholder value for user to identify field and provide relevant entry."
                          className="text-ds-gray-500 font-semibold text-xs "
                        />
                      </div>
                    )}
                  </>
                )}
            </>
          )}

          {selected === 'table' && (
            <>
              <div>
                <NumberInput
                  label="Number of Columns"
                  description="Number of columns within the table."
                  value={columns}
                  onChange={setColumns}
                  min={1}
                  max={100}
                  required
                />
              </div>
              <div className="mt-2">
                <NumberInput
                  label="Number of Rows"
                  description="Number of rows within the table."
                  value={rows}
                  onChange={setRows}
                  min={1}
                  max={100}
                  required
                />
              </div>

              <div className="mb-4 mt-4">
                <CustomToggle
                  label="Row Header"
                  description="Enable this is if the first columns within in the column is row header."
                  checked={isRowsHeader}
                  onChange={setIsRowsHeader}
                  className="pr-3"
                />
              </div>
              <div className="mb-4">
                <CustomToggle
                  label="Column Header"
                  description="Enable this is if the first row within table is column header."
                  checked={isColumnsHeader}
                  onChange={setIsColumnsHeader}
                  className="pr-3"
                />
              </div>
              <div className="mb-4">
                <CustomToggle
                  label="Column Grouping"
                  description="Enable grouping of related columns under a common header."
                  checked={isColumnsGrouping}
                  onChange={setIsColumnsGrouping}
                  className="pr-3"
                />
              </div>
              <div className="mb-4">
                <CustomToggle
                  label="Additional Rows"
                  description="Enable if the user will have access to add more rows to the table."
                  checked={isAdditionalRows}
                  onChange={setIsAdditionalRows}
                  className="pr-3"
                />
              </div>
              <div className="mb-4">
                <CustomToggle
                  label="Data Auto-population"
                  description="Allows user to auto-populate data to the specific field with the help of identifier."
                  checked={isPopulateData}
                  onChange={setIsPopulateData}
                  className="pr-3"
                />
              </div>
            </>
          )}
          {selected === 'tabs' && (
            <div>
              <NumberInput
                label="Number of Tabs"
                description="Select or enter the number of tabs."
                value={tabs}
                onChange={setTabs}
                min={1}
                max={100}
                required
              />
            </div>
          )}
        </div>

        <div className="border-l border-ds-gray-100"></div>

        {selected !== '' && (
          <>
            <div className="w-[51%] p-1 overflow-y-auto pt-0">
              {selected === 'text_input' && (
                <TextDataType
                  onDataChange={handleChildDataChange}
                  defaultValue={formData.defaultValue}
                  helpText={formData.helpText}
                />
              )}
              {selected === 'text_area' && (
                <TextAreaDataType
                  onDataChange={handleChildDataChange}
                  defaultValue={formData.defaultValue}
                  helpText={formData.helpText}
                />
              )}
              {selected === 'dropdown' && (
                <DropdownDataType
                  errors={errors}
                  onDataChange={handleChildDataChange}
                  option={formData.options}
                  helpText={formData.helpText}
                />
              )}
              {selected === 'condition_with_multiple_fields' && (
                <ConditionDataType
                  onDataChange={handleChildDataChange}
                  shouldValidate={shouldValidate}
                  option={formData.options}
                  helpText={formData.helpText}
                  defaultValue={formData?.defaultValue}
                />
              )}
              {selected === 'information' && (
                <InformationDataType
                  onDataChange={handleChildDataChange}
                  isModal={isModal}
                  errors={errors}
                  setErrors={setErrors}
                  modalContent={formData.modalContent}
                  informationContainerContent={formData.informationContainerContent}
                  isPopulateData={isPopulateData}
                  tabName={formData?.tabName}
                />
              )}
              {selected === 'date' && (
                <DateDataType
                  onDataChange={handleChildDataChange}
                  dateFormat={formData.date}
                  helpText={formData.helpText}
                />
              )}
              {selected === 'table' && (
                <TableDataType
                  numColumns={columns}
                  numRows={rows}
                  showRowConfig={isRowsHeader}
                  showColumnConfig={isColumnsHeader}
                  showGroupConfig={isColumnsGrouping}
                  onChange={handleTableDataChange}
                  initialData={tableData}
                  errors={tableErrors}
                  isPopulateData={isPopulateData}
                />
              )}
              {selected === 'tabs' && (
                <TabsDataType
                  numTabs={tabs}
                  onChange={handleTabsDataChange}
                  initialData={tableData}
                  errors={tabsErrors}
                />
              )}

              {selected === 'file' && (
                <UploadFileDataType
                  onDataChange={handleChildDataChange}
                  helpText={formData.helpText}
                />
              )}

              {selected === 'label' && (
                <LabelDataType onDataChange={handleChildDataChange} helpText={formData.helpText} />
              )}
              {selected === 'readonly_text_area' && (
                <ReadOnlyTextAreaType
                  onDataChange={handleChildDataChange}
                  defaultValue={formData.defaultValue}
                />
              )}
            </div>
          </>
        )}

        {selected === '' && (
          <h3 className="text-ds-cool-gray-400 flex items-center m-auto">
            Please select input type
          </h3>
        )}
      </div>

      <div className="flex justify-between py-4 mb-4 gap-3">
        <div>
          <Button color="outlined" size="sm" onClick={handleClearAll}>
            Clear All
          </Button>
        </div>
        <div className="flex gap-2 mr-4">
          <Button color="outlined" size="sm" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            color="primary"
            size={isEditMode ? 'md' : 'sm'}
            onClick={handleSaveField}
            loading={addLoading || updateLoading || addTabsLoading || updateTabsLoading}
          >
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddField;
